import { ReactNode } from "react";

import { YearlyAllocations } from "@src/types/role_request_types";
import { ColDef } from "ag-grid-community";
import { TransferProps } from "antd";
import { TransferItem } from "antd/es/transfer";

import {
  NOTIFICATION_TYPE,
  ProjectTypeEnum,
  TeamLeadSolutionType,
} from "./constants";
import { TabItem } from "./types/antd_types";
import { AllocationRowData } from "./types/role_request_types";

export enum BlockerState {
  BLOCKED = "blocked",
}

export interface Country {
  countryId: number;
  countryName: string;
  regionName?: string;
}

export interface Region {
  regionId: number;
  regionName: string;
}

export interface Employee {
  employeeId: number;
  fullName: string;
  firstName: string;
  lastName: string;
  applicationRoles: string[];
  deskId: string;
  uiNumber: string;
  rcdNumber: string;
  companyName?: string;
  positionText?: string;
  positionStartDate?: Date;
  positionEndDate?: Date;
  email: string;
  totalAvailablePercentage: number;
  contractType: string;
  costCenter?: string;
  genericRoleName?: string;
  genericRoleId?: number;
  departmentId: number;
  departmentCode: string;
  sapData: boolean;
  isDeputy: boolean;
  located?: string;
  teamLeadId: number;
  teamLeadFullName?: string;
  uiNumberBoss: string;
  primaryRoles?: number[];
}

export interface EmployeeV2 {
  employeeId: number;
  fullName: string;
  standardRoleName: string;
}

export interface Page<T> {
  content: T[];
  totalPages: number;
  totalElements: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  size: number;
  number: number;
}

export type SortDirection = "asc" | "desc";

export interface SortSpec<TField extends string> {
  field: TField;
  direction: SortDirection;
}

export interface PageableRequestParams {
  page?: number;
  size?: number;
}

export interface PageableRequest<T, U extends SortSpec<any>> {
  pageRequestParams?: PageableRequestParams;
  sortRequestParams?: U[];
  filterParams?: T;
}

export interface EmployeeSearchFilters {
  employeeIds?: number[];
  fullName?: string;
  uiNumber?: string;
  positionId?: string;
  orgCodes?: string[];
  roles?: string[];
  standardRoles?: string[];
  teamLeadFullNames?: string[];
}

export type EmployeeFields =
  | "fullName"
  | "uiNumber"
  | "positionId"
  | "orgCode"
  | "role"
  | "standardRole"
  | "teamLeadFullName";

export type EmployeeSortSpec = SortSpec<EmployeeFields>;

export type EmployeeSearchRequest = PageableRequest<
  EmployeeSearchFilters,
  EmployeeSortSpec
>;

export interface Project {
  id: number;
  name: string;
  projectPhase: string;
  countryId: number;
  country: string;
  regionName: string;
  projectManager: Employee;
  startDate: Date;
  endDate: Date;
  lastEdit: Date;
  stageGateDate: Date;
  projectEditors: Employee[];
  jointVenturePartner?: string;
  technologies: string[];
  projectType: ProjectTypeEnum;
  sendRequestsTotal: number;
  inRecruitmentRequestsTotal: number;
  outdatedRequestsTotal: number;
  openRequestsTotal: number;
  requestedRequestsTotal: number;
  confirmedRequestsTotal: number;
  proposedRequestsTotal: number;
  declinedRequestsTotal: number;
  inProgressRequestsTotal: number;
  actionNeededTotal: number;
}

export interface Department {
  departmentId: number;
  orgUnit: string;
  orgName: string;
  departmentLevel: string;
}

export interface CreateDepartmentPayload {
  departmentCode: string;
  departmentName: string;
}

export interface ProjectRoleRequest {
  childId: number;
  genericRoleId: number;
  genericRoleName: string;
  status: string;
  projectRoleRequestId: number;
  projectIdentifier: string;
  scope: string;
  specificRole: string;
  yearlyAllocations: any;
  proposedYearlyAllocations?: any;
  jointVenture?: boolean;
  isOutdated: boolean;
  parentRequestId?: number;
  daysUntilDeletion?: number;
}

export interface TeamLeadRequestOverviewItem {
  projectRoleRequestId: number;
  requestedRoleName: string;
  genericRole: string;
  basicRoleName: string;
  assignedTeamMember?: string;
  roleSpecificationName: string;
  primaryTeamLeadEmployeeId: number;
  projectLeadEmployeeName?: string;
  projectName: string;
  projectPhase: string;
  country: string;
  region: string;
  requestSentDate: Date;
  status: string;
  isUpdated: boolean;
  childRequestId?: number;
  actionRequired: boolean;
  isDeclined: boolean;
  isHandedOver: boolean;
  projectIdentifier: string;
  isCommentRead: boolean;
  isCommentAvailable: boolean;
  outdated: boolean;
  roleRequestStart: Date;
  lastUpdatedDate: Date;
  technologies: string[];
  countryName: string;
  regionName: string;
  departmentCode: string;
  daysUntilDeletion?: number;
  latestComment?: string;
}

export interface TimeBlocker {
  teamMemberId: number;
  specificRole: string;
  isNewTimeBlocker: boolean;
  projectRoleRequestId: number;
  projectName: string;
  allocations: Allocation[];
}

export interface TimeBlockerRequestPayload {
  timeBlockers: Allocation[];
  timeBlockerDescription: string;
}

export interface TimeBlockerFormValue {
  value: number;
  allocations: Allocation[];
  isFieldTouched?: boolean;
}

/**
 * @deprecated use Allocation (or YearlyAllocations) from src/types/role_request_types.ts instead
 */
export interface Allocation {
  year: number;
  month: number;
  date: string;
  requiredPercentage: number;
}

export interface SuggestWorkloadPayload {
  id: number;
  selectedMemberId?: number;
  requestAllocationPayload: ProposeAllocation[];
}

export interface ProposeTeamMemberPayload {
  roleRequestId: number;
  teamMemberId: number;
}

export interface TeamMemberAllocationDetails {
  employeeId: number;
  fullName: string;
  genericRole: GenericRole;
  projects: ProjectAllocationDetails[];
  isVacancy: boolean;
  positionText: string;
  totalAvailabilityPercentage: number;
  genericRoleName: string;
  genericRoleId: number;
}

export interface ProjectAllocationDetails {
  projectRoleRequestId: number;
  projectId: number;
  projectName: string;
  specificRole: string;
  allocations: Allocation[];
  status: string;
}

export interface ModalMsg {
  title: string;
  msg: ReactNode;
  action?: string;
  btnStatus?: boolean;
  size?: string;
  leaveCommentFor?: any;
}

export interface TeamMemberCapacityV2 {
  employeeId: number;
  employeeName: string;
  standardRoleName: string;
  positionText: string;
  positionStartDate: Date;
  positionEndDate: Date;
  positionId: string;
  contractType: string;
  capacities: CapacityV2[];
}

export interface CapacityV2 {
  date: Date;
  capacity: number;
}

export interface SpecificationSplits {
  splits: number[];
}

export interface Note {
  id?: number;
  text: string;
  employeeFullName: string;
  employeeId: number;
  roleRequestId: number;
  createdDate: string;
  createdBy: number;
}

export interface NoteHistory {
  noteHistoryId?: number;
  noteId: number;
  requestId: number;
  readByEmployeeId: number;
}

export interface RequestReplies extends ProjectRoleRequest {
  primaryTeamleadEmployeeId: number;
  teamLeadFullName: string;
  assignedTeamMember: string;
  assignedTeammemberId: number;
  assignedTeammemberDTO: Employee;
  requestSentDate: Date;
  teamLead: string;
  contractType: string;
  projectDTO: Project;
  countryName: string;
  isProposalWithdrawn: boolean;
  isEmployeeDeleted: boolean;
  isReadByEmployee: boolean;
  isCommentAvailable: boolean;
  teamLeadEmployeeDTO: Employee;
  projectName: string;
  projectManager: string;
  isTlChangedOfAssignedEmployee: boolean;
  forwardedTeamlead?: string;
  isJointVenture?: boolean;
}

export interface AdminUploadRequest {
  rowNumber: number;
  basicRoleName: string;
  roleSpecificationName: string;
  givenStandardRoleName: string;
  isValidStandardRole: boolean;
  projectIdentifier?: string;
  specificRole?: string;
  scope?: string;
  standardRoleId: number;
  allocations: AdminUploadAllocation[];
}

export interface AdminUploadAllocation {
  date: string;
  allocation: string;
}

export interface SapCsvData {
  employeeId: number;
  standardRoleId: number;
  companyName: string;
  contractType: string;
  costCenter: string;
  departmentCode: string;
  departmentName: string;
  email: string;
  firstName: string;
  fullName: string;
  lastName: string;
  located: string;
  positionEndDate: Date;
  positionId: string;
  positionStartDate: Date;
  positionText: string;
  rcdNumber: string;
  rcdNumberBoss: string;
  totalAvailablePercentage: number;
  uiNumber: string;
  uiNumberBoss: string;
  teamLeadFullname: string;
  teamLeadOrgCode: string;
  status: string;
  errorMessage: string[];
  applicationRoles: any[];
  currentApplicationRoles: any[];
  isTeamLead: boolean;
}

export interface ProjectPayload {
  projectId: number;
  projectManager: number;
  editors: number[];
  name: string;
  phase: string;
  countryId: number;
  startDate: string;
  endDate: string;
  stageGateDate: string;
  jointVenturePartner: string;
  technologies: string[];
  projectType: string;
}

export interface EditRequestPayload {
  projectId: number;
  editedRequests: UpdateRequest[];
}

export interface SaveRequestPayload {
  projectId: number;
  updatedRequest: UpdateRequest;
}

export interface DeleteRequest {
  requestID: number;
}

export interface UpdateRequest {
  uniqueId: string;
  requestID: number;
  originRequestId: number;
  genericRoleId: number;
  oldGenericRoleId: number;
  columnName: string;
  scope: string;
  value: string;
  projectIdentifier: string;
  requestedYear: number;
  requestedMonth: number;
  allocation: number;
  isJointVenture: boolean;
  isAllocationRequest: boolean;
}

export interface SendRequestPayload {
  roleRequestId: number;
  genericRoleId: number;
}

export interface RequestsToSplit {
  standardRoleId: number;
  requestIdsToReassign: string[];
}

export interface ReassignTeamLeadPayload {
  oldLeadId: number;
  newLeadId: number;
}

export interface ReassignProjectLeadPayload {
  oldLeadId: number;
  newProjectLeadId: number;
}

export interface SelectedLead {
  oldLeadId: number;
  newProjectLeadId?: number;
  newTeamLeadId?: number;
}

export interface RoleAssignment {
  description: string;
  roleSpecificationCategory: any;
  roleSpecificationId: number;
  roleSpecificationName: string;
  standardRoleId: number;
  teamLeadId: number;
}

export interface BasicRoleWithRoleAssignments {
  basicRoleName?: string;
  roleAssignments?: RoleAssignment[];
}

export interface NotificationsCount {
  pageTitle: string;
  notificationCount: number;
}

export interface Route {
  title: string;
  route?: string;
  rootRoute?: string;
  disabled: boolean;
  appRoles?: string[];
  notificationName?: string;
  childItems?: Route[];
}

export interface UploadStatistics {
  totalAdded: number;
  totalUpdated: number;
  totalUnchanged: number;
  totalErrors: number;
  totalDeleted: number;
}

export interface ProgressLoadingMessage {
  text: string;
  totalRows: number;
  currentRowStatus: number;
  percent: number;
  type: string;
}

export interface BasicRole {
  basicRoleId: number;
  basicRoleName: string;
  area?: Area;
  isAssignedToStandardRoles?: boolean;
}

export interface RoleSpecification {
  roleSpecificationId: number;
  roleSpecificationName: string;
  roleSpecificationCategory?: string;
  isAssignedToStandardRoles?: boolean;
}

export interface StandardRoleSearchFilters {
  searchTerm?: string;
}

export type StandardRoleSearchRequest = PageableRequest<
  StandardRoleSearchFilters,
  never
>;

export interface StandardRole {
  standardRoleId: number;
  standardRoleName: string;
  basicRole: BasicRole;
  roleSpecification?: RoleSpecification;
  description: string;
  onlyVisibleForAdmin: boolean;
  primaryTeamLeadEmployeeId: number;
  teamLeadFullName: string;
  teamLead: Employee;
  assignments?: string[];
  department?: Department;
}

export interface GenericRole
  extends Omit<StandardRole, "standardRoleId" | "standardRoleName"> {
  genericRoleId: number;
  genericRoleName: string;
  primaryTeamleadEmployeeFullName: string;
}

export interface StandardRoleForm {
  basicRoleId: number;
  roleSpecificationId: number;
  teamLeadId: number;
  description: string;
  visibility: boolean;
  departmentId: number;
}

export interface ApplicationRole {
  applicationRoleId: number;
  applicationRoleName: string;
}

export interface EmployeeDeleteStatus {
  employeeId: number;
  fullName: string;
  teamleadAndNotDeleteable: boolean;
  projectLeadAndNotDeleteable: boolean;
  errorMessage: string[];
  deleteStatus?: string;
}

export interface FilterPanel {
  labels: string[];
  header: string;
  key: number;
  checkedLabels?: string[];
  filterType: string;
}

export interface ActiveFilter {
  displayName?: string; // optional name override for the filter
  filterType: string;
  filterValue: string;
}

export interface DashboardFilterPayload {
  managerId: number;
  monthRange?: number;
  countries?: string[];
  regions?: string[];
  projects?: string[];
  standardRoles?: string[];
}

export interface Area {
  areaId: number;
  areaName: string;
}

/** payloads */

export interface ProjectPayload {
  projectId: number;
  projectManager: number;
  editors: number[];
  name: string;
  phase: string;
  countryId: number;
  startDate: string;
  endDate: string;
  stageGateDate: string;
  jointVenturePartner: string;
  technologies: string[];
  projectType: string;
}

export interface AssignRolesPayload {
  employeeId: number;
  applicationRoles: string[];
  tlPrimaryGenericRoles: number[];
  tmGenericRole: number;
}

export interface AssignTeamMemberStandardRolesPayload {
  employeeId: number;
  assignedRoleObject: { tmGenericRole: number };
}

export interface UpdateEmployeePayload {
  selectedEmployee: Employee;
  setRequestsToRequested: boolean;
}

export interface RoleAssignmentFilterData {
  basicRoles?: BasicRole[];
  roleSpecifications?: RoleSpecification[];
  standardRoles?: StandardRole[];
  numberOfErrors?: number;
}

export interface ProjectRoleRequestsOfStandardRolePayLoad {
  standardRoleId: number;
  showChildRequest: boolean;
  showTimeBlocker: boolean;
}

export interface AllocationDetails {
  allocationDetailsId?: number;
  date?: Date;
  month?: number;
  projectRoleRequestId?: number;
  proposedPercentage?: number;
  requiredPercentage?: number;
  year?: number;
}

// store types
export interface SplitRoleState {
  currentAssignedRequestsToSplit: Record<string, string[]>;
  unassignedRequests: TransferItem[];
  allRequestsToReassign: TransferItem[];
  targetKeys: TransferProps["targetKeys"];
  isSaveDisabled: boolean;
  isLastStepStateInitialized: boolean;
}

export interface ColumnConfig {
  key: string;
  width?: number;
}

export interface BaseTableState<T, U, V> {
  quickFilter: string;
  isFilterApplied: boolean;
  currentTabConfig?: TabItem;
  filterResult: T[];
  customTableFilters: U;
  customDateFilters?: V;
  columns: string[];
  columnConfigs?: ColumnConfig[];
  isTableDisabled?: boolean;
}

export type BaseTableFilters = Record<string, string[] | boolean>;

export enum REQUEST_OVERVIEW_FILTER_TYPES {
  PROJECT_PHASE = "projectPhase",
  PROJECT_NAME = "projectName",
  REQUESTED_ROLE = "requestedRoleName",
  COUNTRY = "country",
  REGION = "region",
  STATUS = "status",
  TECHNOLOGIES = "technologies",
  REQUEST_START = "roleRequestStart",
  REQUEST_SENT = "requestSentDate",
  REQUIRED_ACTION = "actionRequired",
}

export interface RequestOverviewTableFilters extends BaseTableFilters {
  [REQUEST_OVERVIEW_FILTER_TYPES.COUNTRY]: string[];
  [REQUEST_OVERVIEW_FILTER_TYPES.REGION]: string[];
  [REQUEST_OVERVIEW_FILTER_TYPES.TECHNOLOGIES]: string[];
}

export interface RequestOverviewDateFilters extends BaseTableFilters {
  [REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_START]: string[];
  [REQUEST_OVERVIEW_FILTER_TYPES.REQUEST_SENT]: string[];
}

export interface ModalSettings {
  detailsModalVisible: boolean;
  activeTabKey: string;
}
export interface RequestOverviewState {
  selectedRequestId: number;
  modalSettings: ModalSettings;
  requestOverviewTableState: BaseTableState<
    TeamLeadRequestOverviewItem,
    RequestOverviewTableFilters,
    RequestOverviewDateFilters
  >;
}

/**
 * @deprecated
 */
export interface FeatureToggleState {
  featureToggleState: {
    toggledFeatures: Record<string, boolean>;
  };
}

export interface TableSettingsCompactViewConfig {
  defaultCompacted: boolean;
  compactedColumns: string[];
  expandedColumns: string[];
}

export interface TableSettingsConfig {
  columns: ColDef[];
  selectedColumns?: string[];
  lockedColumns?: string[];
  onChange: (columns: string[]) => void;
  columnsToHide?: string[];
  defaultHiddenColumns?: string[];
  compactViewSettings?: TableSettingsCompactViewConfig;
}

export interface RequestOverviewDetails {
  assignedEmployeeEntity: any | null;
  assignedTeamMember: string | null;
  childRequest: RequestOverviewDetails | null;
  country: string;
  region: string;
  currentAssignedTeamLeadId: number;
  currentAssignedTeamLeadName: string;
  genericRole: string;
  genericRoleId: number;
  isActionRequired: boolean;
  isCurrent: boolean;
  isDeclined: boolean;
  isEmployeeDeleted: boolean;
  isHandedOver: boolean;
  isOutdated: boolean;
  isChangedOfAssignedEmployee: boolean;
  isOnlyFreeTextFieldChanged: boolean;
  isTlChangedOfAssignedEmployee: boolean;
  primaryTeamLeadEmployeeId: number;
  primaryTeamLeadEmployeeName: string;
  projectId: number;
  projectIdentifier: string;
  projectLead: string;
  projectName: string;
  projectPhase: string;
  projectRoleRequestId: number;
  proposedYearlyAllocations: YearlyAllocations;
  requestSentDate: string;
  roleAllocationDetails: AllocationDetails[];
  scopeDescription: string;
  sendBy: Employee;
  stageGateDate: string;
  handedOverBy: Employee;
  specificRole: string;
  stageDate: string;
  status: string;
  technologies: any | null;
  yearlyAllocations: YearlyAllocations;
  workloadStartDate: Date;
}

export type ProposeSolutionSettings = {
  type: TeamLeadSolutionType;
  label: string;
  disabled?: boolean;
  disabledTooltip?: string;
  showOption?: boolean;
};

export interface UpdateInRecruitmentPayload {
  roleRequestId: number;
  inRecruitment: boolean;
}

/**
 * in the backend this is the old RoleRequestDTO which is the type UpdateRequest here in the frontend.
 * So this is basically a subtype of UpdateRequest, but for proposing allocations only these fields are used
 * hopefully this will be tidy up in EPIC 107287
 */
export type ProposeAllocation = {
  allocation: number;
  isAllocationRequest: boolean;
  requestedMonth: number;
  requestedYear: number;
  uniqueId: string;
};

export type NextStepModalState = {
  nextStepModalVisible: boolean;
  proposedSolutionType: string | null;
  confirmationButtonDisabled: boolean;
  proposeTeamMember: {
    proposedAllocations: Map<string, AllocationRowData> | null;
    selectedTeamMemberId: number | null;
  };
  forward: {
    forwardedTeamLeadId: number | null;
  };
  decline: {
    proposedAllocations: Map<string, AllocationRowData> | null;
  };
};

export type ResourcePlanDetailsModalState = {
  resourcePlanModalVisible: boolean;
  selectedResourcePlanRequestId: number | null;
  currentTab: string;
  selectedStandardRoleId?: number;
  additionalInformation?: AdditionalInformation;
  jointVenture?: boolean;
};

export type AdditionalInformation = {
  projectIdentifier: string;
  specificRole: string;
  scope: string;
};

export interface Deputy {
  deputy: number;
  standardRoles: number[];
  standardRolesObjects: StandardRole[];
}

export interface DeputyOldAndNewDTO {
  oldDeputyValues: Deputy[];
  newDeputyValues: Deputy[];
}

export interface AlertConfig {
  condition: boolean;
  message: React.ReactNode;
  type: NOTIFICATION_TYPE.INFO | NOTIFICATION_TYPE.WARNING;
  action?: React.ReactNode;
}

export type MutationLike = (arg: any) => Promise<any> & {
  unwrap: () => Promise<any>;
};

export type LazyQueryLike<TResult, TArg = void> = (arg: TArg) => Promise<{
  data?: TResult;
  error?: any;
}>;
